import { normalizeSkillKey, SKILL_FACET_DISPLAY_LIMIT, type TalentFacet } from '@/lib/talent-browse-filters'
import type { EmployerRecentApplicantRow } from '@/hooks/use-managed-org-recent-applicants'

export type EmployerTalentSort =
  | 'applied_desc'
  | 'applied_asc'
  | 'name_asc'
  | 'name_desc'
  | 'match_desc'

export type EmployerApplicantBrowseFilters = {
  textQuery: string
  selectedSkills: string[]
  opportunityId: string
  sort: EmployerTalentSort
}

export const DEFAULT_EMPLOYER_APPLICANT_BROWSE_FILTERS: EmployerApplicantBrowseFilters = {
  textQuery: '',
  selectedSkills: [],
  opportunityId: '',
  sort: 'applied_desc',
}

export function parseEmployerTalentSort(value: string | null): EmployerTalentSort {
  if (
    value === 'applied_asc' ||
    value === 'name_asc' ||
    value === 'name_desc' ||
    value === 'match_desc'
  ) {
    return value
  }
  return 'applied_desc'
}

export function parseEmployerApplicantBrowseFiltersFromSearchParams(
  searchParams: URLSearchParams,
): EmployerApplicantBrowseFilters {
  const skillsParam = searchParams.get('skills')?.trim() ?? ''
  const selectedSkills = skillsParam
    ? skillsParam
        .split(',')
        .map((skill) => skill.trim())
        .filter(Boolean)
    : []

  return {
    textQuery: searchParams.get('q')?.trim() ?? '',
    selectedSkills,
    opportunityId: searchParams.get('opportunity_id')?.trim() ?? '',
    sort: parseEmployerTalentSort(searchParams.get('sort')),
  }
}

export function applyEmployerApplicantBrowseFiltersToSearchParams(
  base: URLSearchParams,
  filters: EmployerApplicantBrowseFilters,
): URLSearchParams {
  const next = new URLSearchParams(base)
  next.delete('q')
  next.delete('skills')
  next.delete('opportunity_id')
  next.delete('sort')

  if (filters.textQuery.trim()) next.set('q', filters.textQuery.trim())
  if (filters.selectedSkills.length > 0) next.set('skills', filters.selectedSkills.join(','))
  if (filters.opportunityId.trim()) next.set('opportunity_id', filters.opportunityId.trim())
  if (filters.sort !== 'applied_desc') next.set('sort', filters.sort)

  return next
}

export function buildEmployerApplicantSkillFacets(
  applicants: EmployerRecentApplicantRow[],
  filters?: EmployerApplicantBrowseFilters,
  limit = SKILL_FACET_DISPLAY_LIMIT,
): TalentFacet[] {
  const counts = new Map<string, { label: string; count: number }>()

  for (const row of applicants) {
    const skills = row.profile.top_skills ?? []
    for (const skill of skills) {
      const key = normalizeSkillKey(skill)
      const existing = counts.get(key)
      if (existing) {
        existing.count += 1
      } else {
        counts.set(key, { label: skill, count: 1 })
      }
    }
  }

  const selectedKeys = new Set((filters?.selectedSkills ?? []).map(normalizeSkillKey))

  return Array.from(counts.entries())
    .map(([key, { label, count }]) => ({ key, label, count }))
    .sort((a, b) => {
      const aSelected = selectedKeys.has(a.key)
      const bSelected = selectedKeys.has(b.key)
      if (aSelected !== bSelected) return aSelected ? -1 : 1
      if (b.count !== a.count) return b.count - a.count
      return a.label.localeCompare(b.label)
    })
    .slice(0, limit)
}

export function buildEmployerApplicantActiveFilterChips(
  filters: EmployerApplicantBrowseFilters,
): { key: string; label: string }[] {
  const chips: { key: string; label: string }[] = []
  if (filters.textQuery.trim()) {
    chips.push({ key: 'q', label: `Search: ${filters.textQuery.trim()}` })
  }
  for (const skill of filters.selectedSkills) {
    chips.push({ key: `skill:${normalizeSkillKey(skill)}`, label: `Skill: ${skill}` })
  }
  if (filters.opportunityId.trim()) {
    chips.push({ key: 'opportunity_id', label: 'Filtered by job' })
  }
  return chips
}

export function removeEmployerApplicantFilterChipKey(
  filters: EmployerApplicantBrowseFilters,
  key: string,
): EmployerApplicantBrowseFilters {
  if (key === 'q') return { ...filters, textQuery: '' }
  if (key === 'opportunity_id') return { ...filters, opportunityId: '' }
  if (key.startsWith('skill:')) {
    const skillKey = key.slice('skill:'.length)
    return {
      ...filters,
      selectedSkills: filters.selectedSkills.filter(
        (skill) => normalizeSkillKey(skill) !== skillKey,
      ),
    }
  }
  return filters
}

export function employerApplicantBrowseQueryParams(
  filters: EmployerApplicantBrowseFilters,
  limit: number,
): URLSearchParams {
  const params = new URLSearchParams({ limit: String(limit) })
  if (filters.textQuery.trim()) params.set('q', filters.textQuery.trim())
  if (filters.opportunityId.trim()) params.set('opportunity_id', filters.opportunityId.trim())
  if (filters.selectedSkills.length > 0) params.set('skills', filters.selectedSkills.join(','))
  return params
}
